
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useCurrencies } from "@/hooks/useCurrencies";
import { Loader2, Plus, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { CurrencyModal } from "@/components/currencies/CurrencyModal";
import { Currency } from "@/lib/types";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const DEFAULT_CURRENCIES = [
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound", symbol: "£" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥" },
  { code: "SEK", name: "Swedish Krona", symbol: "kr" },
  { code: "NOK", name: "Norwegian Krone", symbol: "kr" },
  { code: "DKK", name: "Danish Krone", symbol: "kr" },
];

export function CurrenciesStep() {
  const { useCurrenciesQuery, useCreateCurrencyMutation } = useCurrencies();
  const { updatePreferences } = useUserPreferences();
  const [isAddingDefaults, setIsAddingDefaults] = useState(false);
  const [currencyModalOpen, setCurrencyModalOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("");
  const [isSavingDefault, setIsSavingDefault] = useState(false);
  
  const { data: currencies = [], isLoading } = useCurrenciesQuery();
  const createMutation = useCreateCurrencyMutation();

  // Set the first currency as the default selection when currencies are loaded
  useEffect(() => {
    if (currencies.length > 0 && !selectedCurrency) {
      setSelectedCurrency(currencies[0].id);
    }
  }, [currencies, selectedCurrency]);

  const handleAddDefaultCurrencies = async () => {
    setIsAddingDefaults(true);
    try {
      // Create default currencies sequentially
      for (const currency of DEFAULT_CURRENCIES) {
        await createMutation.mutateAsync(currency);
      }
      toast.success("Default currencies added successfully");
    } catch (error) {
      console.error("Error adding default currencies:", error);
      toast.error("Failed to add some default currencies");
    } finally {
      setIsAddingDefaults(false);
    }
  };

  const handleCurrencyAdded = (currency: Currency) => {
    toast.success(`Currency "${currency.code}" added successfully`);
    // Set the newly added currency as selected
    setSelectedCurrency(currency.id);
  };

  const handleSaveDefaultCurrency = async () => {
    if (!selectedCurrency) {
      toast.error("Please select a default currency");
      return;
    }

    setIsSavingDefault(true);
    try {
      await updatePreferences({ default_currency_id: selectedCurrency });
      toast.success("Default currency set successfully");
    } catch (error) {
      console.error("Error setting default currency:", error);
      toast.error("Failed to set default currency");
    } finally {
      setIsSavingDefault(false);
    }
  };

  return (
    <div className="space-y-6">
      <p>
        Add currencies that you use for your contracts. You'll be able to set one as your default
        currency, and the system will automatically convert amounts for reporting purposes.
      </p>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : currencies.length > 0 ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {currencies.map((currency) => (
              <Card 
                key={currency.id} 
                className={`overflow-hidden cursor-pointer transition-colors ${selectedCurrency === currency.id ? 'border-primary bg-primary/5' : ''}`}
                onClick={() => setSelectedCurrency(currency.id)}
              >
                <CardContent className="p-4 relative">
                  {selectedCurrency === currency.id && (
                    <CheckCircle2 className="absolute top-2 right-2 h-5 w-5 text-primary" />
                  )}
                  <div className="font-medium flex items-center mt-1">
                    <span className="mr-2">{currency.symbol}</span>
                    <span>{currency.code}</span>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">{currency.name}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="bg-muted p-4 rounded-lg">
            <p className="font-medium mb-2">Select your default currency</p>
            <p className="text-sm text-muted-foreground mb-4">
              This will be used as the base currency for all reports and conversions.
            </p>
            
            <RadioGroup value={selectedCurrency} onValueChange={setSelectedCurrency} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {currencies.map((currency) => (
                <div key={currency.id} className="flex items-center space-x-2">
                  <RadioGroupItem value={currency.id} id={`currency-${currency.id}`} />
                  <Label htmlFor={`currency-${currency.id}`} className="cursor-pointer flex items-center">
                    <span className="mr-1">{currency.symbol}</span> {currency.code} - {currency.name}
                  </Label>
                </div>
              ))}
            </RadioGroup>
            
            <Button 
              onClick={handleSaveDefaultCurrency} 
              disabled={isSavingDefault || !selectedCurrency}
              size="sm"
              className="mt-4"
            >
              {isSavingDefault ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>Set as Default</>
              )}
            </Button>
          </div>

          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              You've already set up {currencies.length} currencies
            </p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setCurrencyModalOpen(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add New Currency
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-muted rounded-lg p-6 text-center space-y-4">
          <p className="mb-4">You don't have any currencies yet. Add default currencies or create your own.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              onClick={handleAddDefaultCurrencies} 
              disabled={isAddingDefaults}
              className="w-full sm:w-auto"
            >
              {isAddingDefaults ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Default Currencies
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setCurrencyModalOpen(true)} 
              className="w-full sm:w-auto"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Currency Manually
            </Button>
          </div>
        </div>
      )}

      <CurrencyModal 
        open={currencyModalOpen} 
        onOpenChange={setCurrencyModalOpen}
        onSuccess={handleCurrencyAdded}
      />
    </div>
  );
}
